"use client"

import { useEffect, useState } from "react"
import DatePicker from "react-datepicker"
import ContentHeader from "../../components/ContentHeader"
import Loader from "../../components/Loader"
import Swal from "sweetalert2"
import axios from "axios"
import "react-datepicker/dist/react-datepicker.css"
import { format, subMonths } from "date-fns"
import { es } from "date-fns/locale"

const DailyEarningsReport = () => {
  const [loading, setLoading] = useState(false)
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [partners, setPartners] = useState([])
  const [selectedPartner, setSelectedPartner] = useState("0")
  const [units, setUnits] = useState([])
  const [selectedUnit, setSelectedUnit] = useState("0")
  const [branches, setBranches] = useState([])
  const [selectedBranch, setSelectedBranch] = useState("0")
  const [dailyData, setDailyData] = useState([])
  const [totalSummary, setTotalSummary] = useState({
    totalEarnings: 0,
    totalAdminTickets: 0,
    totalExpenses: 0,
    netEarnings: 0,
    totalTrips: 0
  })
  const [openDetails, setOpenDetails] = useState({})

  const ENDPOINT = process.env.REACT_APP_URL_ENDPOINT
  const TITLEALERT = process.env.REACT_APP_TITLE_ALERT



  // Función para exportar a Excel
const handleExport = async () => {
  if (!startDate || !endDate) {
    Swal.fire({
      icon: 'warning',
      title: '<div class="title-custom-alert">' + TITLEALERT + '</div>',
      html: '<div class="text-custom-alert">Por favor seleccione un rango de fechas</div>',
    })
    return
  }

  const form = new FormData()
  const user = JSON.parse(localStorage.getItem("dataUser"))
  
  form.append("user_id", user.user_id)
  form.append("start_date", format(startDate, "yyyy-MM-dd"))
  form.append("end_date", format(endDate, "yyyy-MM-dd"))
  
  if (selectedPartner !== "0") {
    form.append("selectedPartner", selectedPartner)
  }
  
  if (selectedUnit !== "0") {
    form.append("selectedUnit", selectedUnit)
  }
  
  if (selectedBranch !== "0") {
    form.append("branch_id", selectedBranch)
  }

  setLoading(true)
  
  try {
    const response = await axios.post(ENDPOINT + "export-daily-earnings-report", form, {
      responseType: 'blob'
    })
    
    // Crear un enlace para descargar el archivo
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `reporte-ganancias-diarias-${format(startDate, "yyyy-MM-dd")}-al-${format(endDate, "yyyy-MM-dd")}.xlsx`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    
  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: '<div class="title-custom-alert">' + TITLEALERT + '</div>',
      html: '<div class="text-custom-alert">Error al exportar los datos: ' + (error.response?.data?.msg || error.message) + '</div>',
    })
  } finally {
    setLoading(false)
  }
}



  // Función auxiliar para formatear números
  const safeNumberFormat = (value) => {
    if (value === null || value === undefined) return '0.00'
    const numericValue = parseFloat(value || '0')
    if (isNaN(numericValue)) return '0.00'
    return numericValue.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return ""
    return format(new Date(dateString), "dd/MM/yyyy", { locale: es })
  }

  // Cargar datos de filtros y establecer fechas por defecto
  useEffect(() => {
    const fetchFiltersData = async () => {
      try {
        const [branchesRes, partnersRes, unitsRes] = await Promise.all([
          axios.get(ENDPOINT + "get-all-branchs-ticket"),
          axios.get(ENDPOINT + "get-all-partners"),
          axios.post(ENDPOINT + "get-all-units", { status_filter: "2", type_filter: "0" }),
        ])

        setBranches(branchesRes.data.data.map((b) => ({
          label: b.name,
          value: b.branch_id,
        })))

        setPartners(partnersRes.data.data.map((p) => ({
          label: p.name_partner,
          value: p.partner_id.toString(),
        })))

        setUnits(unitsRes.data.data.map((u) => ({
          label: u.name,
          value: u.unit_id.toString(),
        })))
        
        // Establecer fechas por defecto: un mes atrás y fecha actual
        const currentDate = new Date();
        const oneMonthAgo = subMonths(currentDate, 1);
        setStartDate(oneMonthAgo);
        setEndDate(currentDate);
      } catch (error) {
        console.error("Error cargando filtros:", error)
      }
    }

    fetchFiltersData()
  }, [])

  // Manejar búsqueda
  const handleSearch = async () => {
    if (!startDate || !endDate) {
      Swal.fire({
        icon: 'warning',
        title: '<div class="title-custom-alert">' + TITLEALERT + '</div>',
        html: '<div class="text-custom-alert">Por favor seleccione un rango de fechas</div>',
      })
      return
    }

    const form = new FormData()
    const user = JSON.parse(localStorage.getItem("dataUser"))
    
    form.append("user_id", user.user_id)
    form.append("start_date", format(startDate, "yyyy-MM-dd"))
    form.append("end_date", format(endDate, "yyyy-MM-dd"))
    
    if (selectedPartner !== "0") {
      form.append("selectedPartner", selectedPartner)
    }
    
    if (selectedUnit !== "0") {
      form.append("selectedUnit", selectedUnit)
    }
    
    if (selectedBranch !== "0") {
      form.append("branch_id", selectedBranch)
    }

    setLoading(true)
    
    try {
      const response = await axios.post(ENDPOINT + "daily-earnings-by-filter", form)
      
      if (response.data.res) {
        setDailyData(response.data.data)
        setOpenDetails({})
        
        // Calcular totales
        const summary = response.data.data.reduce((acc, day) => {
          const adminTotal = day.admin_tickets 
            ? day.admin_tickets.reduce((sum, ticket) => sum + (ticket.total || 0), 0) 
            : 0;
            
          return {
            totalEarnings: acc.totalEarnings + day.total_earnings,
            totalAdminTickets: acc.totalAdminTickets + adminTotal,
            totalExpenses: acc.totalExpenses + day.total_expenses,
            netEarnings: acc.netEarnings + day.net_earnings,
            totalTrips: acc.totalTrips + day.trip_count
          }
        }, { totalEarnings: 0, totalAdminTickets: 0, totalExpenses: 0, netEarnings: 0, totalTrips: 0 })
        
        setTotalSummary(summary)
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: '<div class="title-custom-alert">' + TITLEALERT + '</div>',
        html: '<div class="text-custom-alert">Error al obtener los datos: ' + (error.response?.data?.msg || error.message) + '</div>',
      })
    } finally {
      setLoading(false)
    }
  }

  // Alternar visibilidad de detalles
  const toggleDetails = (index) => {
    setOpenDetails(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  return (
    <div>
      {loading && <Loader />}
      <div className="container-fluid">
        <ContentHeader title="Módulo de Reportes" />
      </div>
      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title m-0 page-title">
              <i className="bi bi-calendar-range me-2"></i> Reporte de Ganancias Diarias por Filtro
            </h4>
          </div>
          <div className="card-body">
            {/* Controles de búsqueda */}
            <div className="row mb-4">
              <div className="col-md-3">
                <label className="form-label fw-bold">Fecha Inicio</label>
                <DatePicker
                  selected={startDate}
                  onChange={setStartDate}
                  className="form-control form-control-lg"
                  dateFormat="dd/MM/yyyy"
                  locale={es}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-bold">Fecha Fin</label>
                <DatePicker
                  selected={endDate}
                  onChange={setEndDate}
                  className="form-control form-control-lg"
                  dateFormat="dd/MM/yyyy"
                  locale={es}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label fw-bold">Sucursal</label>
                <select
                  className="form-control form-control-lg"
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                >
                  <option value="0">Todas</option>
                  {branches.map(branch => (
                    <option key={branch.value} value={branch.value}>{branch.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label fw-bold">Socio</label>
                <select
                  className="form-control form-control-lg"
                  value={selectedPartner}
                  onChange={(e) => setSelectedPartner(e.target.value)}
                >
                  <option value="0">Todos</option>
                  {partners.map(partner => (
                    <option key={partner.value} value={partner.value}>{partner.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label fw-bold">Unidad</label>
                <select
                  className="form-control form-control-lg"
                  value={selectedUnit}
                  onChange={(e) => setSelectedUnit(e.target.value)}
                >
                  <option value="0">Todas</option>
                  {units.map(unit => (
                    <option key={unit.value} value={unit.value}>{unit.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="row mb-4">
  <div className="col-md-12 d-flex justify-content-end gap-2">
    <button 
      className="btn btn-primary btn-lg px-4"
      onClick={handleSearch}
    >
      <i className="fa fa-search me-2"></i> Buscar
    </button>
    {dailyData.length > 0 && (
      <button 
        className="btn btn-success btn-lg px-4"
        onClick={handleExport}
      >
        <i className="fa fa-file-excel me-2"></i> Exportar Excel
      </button>
    )}
  </div>
</div>

            {/* Resumen total */}
            {dailyData.length > 0 && (
              <div className="row mb-4">
                <div className="col-md-2">
                  <div className="card bg-primary text-white">
                    <div className="card-body text-center">
                      <h6>Total Ganancias</h6>
                      <h4>${safeNumberFormat(totalSummary.totalEarnings)}</h4>
                    </div>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="card bg-info text-white">
                    <div className="card-body text-center">
                      <h6>Boletos Admin</h6>
                      <h4>${safeNumberFormat(totalSummary.totalAdminTickets)}</h4>
                    </div>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="card bg-danger text-white">
                    <div className="card-body text-center">
                      <h6>Total Gastos</h6>
                      <h4>${safeNumberFormat(totalSummary.totalExpenses)}</h4>
                    </div>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="card bg-success text-white">
                    <div className="card-body text-center">
                      <h6>Ganancia Neta</h6>
                      <h4>${safeNumberFormat(totalSummary.netEarnings)}</h4>
                    </div>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="card bg-warning text-white">
                    <div className="card-body text-center">
                      <h6>Total Viajes</h6>
                      <h4>{totalSummary.totalTrips}</h4>
                    </div>
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="card bg-secondary text-white">
                    <div className="card-body text-center">
                      <h6>Total General</h6>
                      <h4>${safeNumberFormat(totalSummary.netEarnings + totalSummary.totalAdminTickets)}</h4>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tabla de datos diarios */}
            {dailyData.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead className="table-dark">
                    <tr>
                      <th>Fecha</th>
                      <th>Ganancias</th>
                      <th>Boletos Admin</th>
                      <th>Gastos</th>
                      <th>Ganancia Neta</th>
                      <th>Viajes</th>
                      <th>Detalles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyData.map((day, index) => {
                      const adminTotal = day.admin_tickets 
                        ? day.admin_tickets.reduce((sum, ticket) => sum + (ticket.total || 0), 0) 
                        : 0;
                      
                      return (
                        <>
                          <tr key={index}>
                            <td>{day.date}</td>
                            <td>${safeNumberFormat(day.total_earnings)}</td>
                            <td>${safeNumberFormat(adminTotal)}</td>
                            <td>${safeNumberFormat(day.total_expenses)}</td>
                            <td>${safeNumberFormat(day.net_earnings)}</td>
                            <td>{day.trip_count}</td>
                            <td>
                              <button 
                                className="btn btn-sm btn-info"
                                onClick={() => toggleDetails(index)}
                              >
                                <i className={`fa ${openDetails[index] ? 'fa-eye-slash' : 'fa-eye'}`}></i> 
                                {openDetails[index] ? ' Ocultar' : ' Ver'}
                              </button>
                            </td>
                          </tr>
                          {openDetails[index] && (
                            <tr>
                              <td colSpan="7" className="p-0">
                                <div className="card card-body m-2">
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="m-0">Detalles para {day.date}</h5>
                                    <button 
                                      className="btn btn-sm btn-danger"
                                      onClick={() => toggleDetails(index)}
                                    >
                                      <i className="fa fa-times"></i> Cerrar
                                    </button>
                                  </div>
                                  
                                  {/* Tabla de Tickets */}
                                  <h6 className="mt-3">Boletos Repaticion en Taquilla:</h6>
                                  <table className="table table-sm">
                                    <thead>
                                      <tr>
                                        <th>Horario</th>
                                        <th>Conductor</th>
                                        <th>Letra</th>
                                        <th>Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {day.tickets && day.tickets.map((ticket, ticketIndex) => (
                                        <tr key={ticketIndex}>
                                          <td>{ticket.time}</td>
                                          <td>{ticket.driver}</td>
                                          <td>{ticket.letter}</td>
                                          <td>${safeNumberFormat(ticket.total)}</td>
                                        </tr>
                                      ))}
                                      {day.tickets && day.tickets.length > 0 && (
                                        <tr className="table-warning">
                                          <td colSpan="3" className="text-end"><strong>Total Tickets:</strong></td>
                                          <td><strong>${safeNumberFormat(day.tickets.reduce((sum, t) => sum + (t.total || 0), 0))}</strong></td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>

                                  {/* Tabla de Boletos de Administración */}
                                  {day.admin_tickets && day.admin_tickets.length > 0 && (
                                    <>
                                      <h6 className="mt-4">Boletos de Administración Correspondiente a Cada Unidad Sprinter:</h6>
                                      <table className="table table-sm">
                                        <thead>
                                          <tr>
                                            <th>Horario</th>
                                            <th>Conductor</th>
                                            <th>Letra</th>
                                            <th>Total</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {day.admin_tickets.map((ticket, ticketIndex) => (
                                            <tr key={`admin-${ticketIndex}`}>
                                              <td>{ticket.time}</td>
                                              <td>{ticket.driver}</td>
                                              <td>{ticket.letter}</td>
                                              <td>${safeNumberFormat(ticket.total)}</td>
                                            </tr>
                                          ))}
                                          <tr className="table-warning">
                                            <td colSpan="3" className="text-end"><strong>Total Admin:</strong></td>
                                            <td><strong>${safeNumberFormat(day.admin_tickets.reduce((sum, t) => sum + (t.total || 0), 0))}</strong></td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </>
                                  )}

                                  {/* Tabla de Gastos (Bills) */}
                                  {day.bills && day.bills.length > 0 && (
                                    <>
                                      <h6 className="mt-4">Gastos Detallados:</h6>
                                      <table className="table table-sm table-bordered">
                                        <thead className="table-secondary">
                                          <tr>
                                            <th>Fecha</th>
                                            <th>Concepto</th>
                                            <th>Unidad</th>
                                            <th>Conductor</th>
                                            <th>Usuario</th>                                          
                                            <th>Monto</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {day.bills.map((bill, billIndex) => (
                                            <tr key={billIndex}>
                                              <td>{formatDate(bill.date)}</td>
                                              <td>{bill.concept}</td>
                                              <td>{bill.unit}</td>
                                              <td>{bill.driver}</td>
                                              <td>{bill.user}</td>                                            
                                              <td>${safeNumberFormat(bill.amount)}</td>
                                            </tr>
                                          ))}
                                          <tr className="table-warning">
                                            <td colSpan="5" className="text-end"><strong>Total Gastos:</strong></td>
                                            <td><strong>${safeNumberFormat(day.total_expenses)}</strong></td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </>
                                  )}

                                  {/* Resumen del día */}
                                  <div className="row mt-3">
                                    <div className="col-md-3">
                                      <div className="card bg-light">
                                        <div className="card-body text-center">
                                          <h6>Total Ganancias</h6>
                                          <h5>${safeNumberFormat(day.total_earnings)}</h5>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-3">
                                      <div className="card bg-light">
                                        <div className="card-body text-center">
                                          <h6>Boletos Admin</h6>
                                          <h5>${safeNumberFormat(adminTotal)}</h5>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-3">
                                      <div className="card bg-light">
                                        <div className="card-body text-center">
                                          <h6>Total Gastos</h6>
                                          <h5>${safeNumberFormat(day.total_expenses)}</h5>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-3">
                                      <div className="card bg-light">
                                        <div className="card-body text-center">
                                          <h6>Ganancia Neta</h6>
                                          <h5>${safeNumberFormat(day.net_earnings)}</h5>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="alert alert-info">
                No hay datos para mostrar. Seleccione un rango de fechas y filtros, luego haga clic en Buscar.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DailyEarningsReport